import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ItemEditor from '../components/ItemEditor';
import FormField from '../components/FormField';
import Modal from '../components/Modal';

interface OrderItem {
  id?: number;
  item_name: string;
  quantity: number;
  unit: string;
  unit_price: number;
  amount: number;
  sort_order: number;
}

interface Customer {
  id: number;
  name: string;
  address: string;
  phone: string;
}

interface OrderFormData {
  customer_id: number | null;
  title: string;
  construction_site: string;
  issue_date: string;
  order_date: string;
  start_date: string;
  completion_date: string;
  delivery_date: string;
  payment_terms: string;
  discount: number;
  tax_rate: number;
  tax_rounding: 'floor' | 'round' | 'ceil';
  items: OrderItem[];
}

const defaultFormData: OrderFormData = {
  customer_id: null,
  title: '',
  construction_site: '',
  issue_date: new Date().toISOString().split('T')[0],
  order_date: '',
  start_date: '',
  completion_date: '',
  delivery_date: '',
  payment_terms: '',
  discount: 0,
  tax_rate: 10,
  tax_rounding: 'floor',
  items: [],
};

const OrderEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromEstimateId = searchParams.get('fromEstimate');
  const isNew = !id || id === 'new';

  const [formData, setFormData] = useState<OrderFormData>(defaultFormData);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadCustomers();
    if (!isNew && id) {
      loadOrder(Number(id));
    } else if (fromEstimateId) {
      loadFromEstimate(Number(fromEstimateId));
    }
  }, [id, fromEstimateId]);

  const loadCustomers = async () => {
    try {
      const result = await window.api.customer.list();
      setCustomers(result);
    } catch (err) {
      setError('顧客一覧の読み込みに失敗しました');
    }
  };

  const loadOrder = async (orderId: number) => {
    setLoading(true);
    try {
      const order = await window.api.order.get(orderId);
      setFormData({
        customer_id: order.customer_id,
        title: order.title,
        construction_site: order.construction_site,
        issue_date: order.issue_date,
        order_date: order.order_date,
        start_date: order.start_date,
        completion_date: order.completion_date,
        delivery_date: order.delivery_date,
        payment_terms: order.payment_terms,
        discount: order.discount || 0,
        tax_rate: order.tax_rate || 10,
        tax_rounding: order.tax_rounding || 'floor',
        items: order.items || [],
      });
    } catch (err) {
      setError('注文請書の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadFromEstimate = async (estimateId: number) => {
    setLoading(true);
    try {
      const estimate = await window.api.estimate.get(estimateId);
      setFormData({
        customer_id: estimate.customer_id,
        title: estimate.title,
        construction_site: estimate.construction_site,
        issue_date: new Date().toISOString().split('T')[0],
        order_date: '',
        start_date: estimate.start_date || '',
        completion_date: estimate.completion_date || '',
        delivery_date: '',
        payment_terms: '',
        discount: estimate.discount || 0,
        tax_rate: estimate.tax_rate || 10,
        tax_rounding: estimate.tax_rounding || 'floor',
        items: estimate.items || [],
      });
    } catch (err) {
      setError('見積書の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleItemsChange = useCallback((items: OrderItem[]) => {
    setFormData(prev => ({ ...prev, items }));
  }, []);

  const calculateTotals = useCallback(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const afterDiscount = subtotal - (formData.discount || 0);
    let taxAmount: number;
    const taxRate = formData.tax_rate / 100;
    const rawTax = afterDiscount * taxRate;
    switch (formData.tax_rounding) {
      case 'floor':
        taxAmount = Math.floor(rawTax);
        break;
      case 'ceil':
        taxAmount = Math.ceil(rawTax);
        break;
      case 'round':
        taxAmount = Math.round(rawTax);
        break;
      default:
        taxAmount = Math.floor(rawTax);
    }
    const total = afterDiscount + taxAmount;
    return { subtotal, afterDiscount, taxAmount, total };
  }, [formData.items, formData.discount, formData.tax_rate, formData.tax_rounding]);

  const handleSave = async () => {
    if (!formData.customer_id) {
      setError('顧客を選択してください');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = {
        ...formData,
        customer_id: formData.customer_id,
      };
      if (isNew) {
        const result = await window.api.order.create(data);
        navigate(`/order/${result.id}`);
      } else {
        await window.api.order.update(Number(id), data);
      }
    } catch (err) {
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await window.api.order.delete(Number(id));
      navigate('/orders');
    } catch (err) {
      setError('削除に失敗しました');
    } finally {
      setSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!id && isNew) {
      setError('先に保存してください');
      return;
    }
    try {
      await window.api.order.generatePdf(Number(id));
    } catch (err) {
      setError('PDF生成に失敗しました');
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        読み込み中...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0 }}>
          {isNew ? '新規注文請書' : '注文請書編集'}
        </h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!isNew && (
            <button
              onClick={() => navigate(`/schedule/${id}`)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              工程表を開く
            </button>
          )}
          <button
            onClick={handleGeneratePdf}
            disabled={isNew}
            style={{
              padding: '8px 16px',
              backgroundColor: isNew ? '#ccc' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isNew ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            PDF出力
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 16px',
              backgroundColor: saving ? '#ccc' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {saving ? '保存中...' : '保存'}
          </button>
          {!isNew && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              削除
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <FormField label="顧客" required>
            <select
              value={formData.customer_id || ''}
              onChange={(e) => handleFieldChange('customer_id', e.target.value ? Number(e.target.value) : null)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              <option value="">顧客を選択</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="件名" required>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="工事場所">
            <input
              type="text"
              value={formData.construction_site}
              onChange={(e) => handleFieldChange('construction_site', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="発行日">
            <input
              type="date"
              value={formData.issue_date}
              onChange={(e) => handleFieldChange('issue_date', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="受注日">
            <input
              type="date"
              value={formData.order_date}
              onChange={(e) => handleFieldChange('order_date', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="着工予定日">
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => handleFieldChange('start_date', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="完工予定日">
            <input
              type="date"
              value={formData.completion_date}
              onChange={(e) => handleFieldChange('completion_date', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="納期">
            <input
              type="date"
              value={formData.delivery_date}
              onChange={(e) => handleFieldChange('delivery_date', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </FormField>

          <FormField label="支払条件" style={{ gridColumn: '1 / -1' }}>
            <textarea
              value={formData.payment_terms}
              onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                minHeight: '60px',
              }}
            />
          </FormField>
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>明細</h2>
        <ItemEditor items={formData.items} onChange={handleItemsChange} />
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <FormField label="値引">
            <input
              type="number"
              value={formData.discount}
              onChange={(e) => handleFieldChange('discount', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                textAlign: 'right',
              }}
            />
          </FormField>

          <FormField label="税率">
            <select
              value={formData.tax_rate}
              onChange={(e) => handleFieldChange('tax_rate', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              <option value={10}>10%</option>
              <option value={8}>8%</option>
              <option value={0}>0%</option>
            </select>
          </FormField>

          <FormField label="税端数処理">
            <select
              value={formData.tax_rounding}
              onChange={(e) => handleFieldChange('tax_rounding', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            >
              <option value="floor">切り捨て</option>
              <option value="round">四捨五入</option>
              <option value="ceil">切り上げ</option>
            </select>
          </FormField>
        </div>

        <div style={{ borderTop: '2px solid #333', paddingTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '40px', fontSize: '16px', marginBottom: '8px' }}>
            <span style={{ color: '#666' }}>小計:</span>
            <span style={{ fontWeight: 'bold', minWidth: '120px', textAlign: 'right' }}>
              ¥{totals.subtotal.toLocaleString()}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '40px', fontSize: '16px', marginBottom: '8px' }}>
            <span style={{ color: '#666' }}>値引:</span>
            <span style={{ fontWeight: 'bold', minWidth: '120px', textAlign: 'right' }}>
              -¥{formData.discount.toLocaleString()}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '40px', fontSize: '16px', marginBottom: '8px' }}>
            <span style={{ color: '#666' }}>消費税:</span>
            <span style={{ fontWeight: 'bold', minWidth: '120px', textAlign: 'right' }}>
              ¥{totals.taxAmount.toLocaleString()}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '40px', fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>
            <span>合計:</span>
            <span style={{ minWidth: '120px', textAlign: 'right' }}>
              ¥{totals.total.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <Modal onClose={() => setShowDeleteConfirm(false)}>
          <div style={{ padding: '20px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>削除の確認</h3>
            <p style={{ marginBottom: '20px', color: '#666' }}>この注文請書を削除してもよろしいですか？この操作は元に戻せません。</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: saving ? '#ccc' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? '削除中...' : '削除する'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OrderEdit;
